/**
 * Cache valdymo modulis
 * Skirtas spręsti localStorage QuotaExceededError ir kitas cache problemas
 */

class CacheManager {
  constructor() {
    // Stebėsime localStorage klaidas
    this.setupErrorHandling();
    
    // Periodiniam valymui
    this.initialized = false;
    this.cleanupInterval = null;
  }
  
  /**
   * Inicializuoti cache valdymą
   */
  initialize() {
    if (this.initialized) return;
    
    // Iš karto išvalome senus cache įrašus
    this.cleanupCache();
    
    // Nustatome periodinį valymą (kas 30 minučių)
    this.cleanupInterval = setInterval(() => {
      this.cleanupCache();
    }, 30 * 60 * 1000);
    
    this.initialized = true;
  }
  
  /**
   * Nustatyti klaidos valdiklį
   */
  setupErrorHandling() {
    // Globalus neapdorotų klaidų valdiklis
    window.addEventListener('error', (event) => {
      // Filtruoti lokalios saugyklos klaidas
      if (event.error && 
         (event.error.name === 'QuotaExceededError' || 
          event.error.message.includes('quota'))) {
        event.preventDefault();
        // Išvalyti cache
        this.cleanupCache(true);
        return;
      }
    });
    
    // Neapdorotų pažadų klaidos
    window.addEventListener('unhandledrejection', (event) => {
      // Filtruoti lokalios saugyklos klaidas
      if (event.reason && 
         (event.reason.name === 'QuotaExceededError' || 
          String(event.reason).includes('quota'))) {
        event.preventDefault();
        // Išvalyti cache
        this.cleanupCache(true);
      }
    });
    
    // Nepersirašome localStorage.setItem - tai gali kelti problemų
  }

  /**
   * Saugus localStorage.setItem wrapper
   * @param {string} key 
   * @param {string} value 
   * @returns {boolean} ar pavyko išsaugoti
   */
  static safeSetItem(key, value) {
    try {
      localStorage.setItem(key, value);
      return true;
    } catch (error) {
      if (error.name === 'QuotaExceededError' || error.message.includes('quota')) {
        // Sukuriame cache manager ir valome
        const manager = new CacheManager();
        manager.cleanupCache(true);
        
        try {
          // Bandome dar kartą po išvalymo
          localStorage.setItem(key, value);
          return true;
        } catch (e) {
          console.warn('Failed to save to localStorage even after cleanup:', e);
          return false;
        }
      } else {
        console.error('localStorage error:', error);
        return false;
      }
    }
  }
  
  /**
   * Išvalyti cache
   * @param {boolean} aggressive - ar naudoti agresyvų valymą 
   */
  cleanupCache(aggressive = false) {
    let cleanedCount = 0;
    try {
      const keys = Object.keys(localStorage);
      
      // Senų įrašų ribos amžius (14 dienų įprastai, 1 diena agresyviam valymui)
      const MAX_AGE_MS = aggressive ? 24 * 60 * 60 * 1000 : 14 * 24 * 60 * 60 * 1000;
      const now = Date.now();
      
      // Valome TIK senus arba neanalizuojamus įrašus, **NIEKADA** neliečiame Supabase raktų
      keys.forEach(key => {
        // **IGNORUOJAME Supabase raktus**
        if (key.startsWith('sb-') || key.includes('supabase')) {
          return; // Praleidžiame Supabase įrašus
        }

        // Ar tai senas įrašas su timestamp?
        try {
          const item = JSON.parse(localStorage.getItem(key));
          if (item && item.timestamp && (now - item.timestamp > MAX_AGE_MS)) {
            localStorage.removeItem(key);
            cleanedCount++; // Debug log
          }
        } catch (e) {
          // Jei nepavyksta JSON.parse, tai arba ne JSON, arba sugadintas įrašas
          // Agresyviam valymui pašaliname bet kokius įrašus, kurių nepavyksta analizuoti (išskyrus Supabase)
          if (aggressive) {
            localStorage.removeItem(key);
            cleanedCount++; // Debug log
          }
        }
      });
      
      // Jei vis dar turime viršijimo problemą ir agresyvus valymas, šaliname bet ką, KAS NĖRA Supabase
      if (aggressive && this.isStorageNearlyFull()) {
        console.warn('LocalStorage still nearly full after initial aggressive cleanup. Removing non-essential items.'); // Debug log
        // Šaliname viską, išskyrus kritinius ir Supabase įrašus
        keys.forEach(key => {
          // Palikti tik esminius IR Supabase įrašus
          if (!key.startsWith('sb-') && !key.includes('supabase') && !key.includes('essential-') && !key.includes('critical-')) {
            localStorage.removeItem(key);
            cleanedCount++; // Debug log
          }
        });
      }
      if (cleanedCount > 0) {
      }
    } catch (e) {
      console.error("Error during cleanupCache:", e); // Pakeista tylų praleidimą į klaidos registravimą
    }
  }
  
  /**
   * Ar localStorage beveik pilnas
   * @returns {boolean}
   */
  isStorageNearlyFull() {
    try {
      // Tipiška localStorage talpa naršyklėse yra apie 5MB
      // Laikysime, kad 4.5MB yra "beveik pilnas"
      const MAX_SIZE = 4.5 * 1024 * 1024; // 4.5MB
      
      let totalSize = 0;
      for (let i = 0; i < localStorage.length; i++) {
        const key = localStorage.key(i);
        const value = localStorage.getItem(key);
        totalSize += (key.length + value.length) * 2; // UTF-16 = 2 bytes per char
      }
      
      return totalSize > MAX_SIZE;
    } catch (e) {
      // Jei klaida gaunant localStorage dydį, laikome, kad jis beveik pilnas
      return true;
    }
  }
  
  /**
   * Sunaikinti valdiklį ir išvalyti intervalą
   */
  destroy() {
    if (this.cleanupInterval) {
      clearInterval(this.cleanupInterval);
      this.cleanupInterval = null;
    }
    this.initialized = false;
  }
}

// Sukuriame ir eksportuojame instanciją
const cacheManager = new CacheManager();

// Eksportuojame ir saugų localStorage metodą
export { CacheManager };
export default cacheManager;
